import { forwardRef, useEffect, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@shared/hooks';
import { cn, PaletteUtils } from '@shared/utils';
import { Icon } from '@shared/widgets';
import SearchIcon from '@assets/icons/search.svg';
import styles from './styles.module.css';

interface ISearchInputProps {
  className?: string;
  inputClassName?: string;
  readOnly?: boolean;
  value?: string;
  onChange: (data: string) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
}

export const SearchInput = forwardRef<HTMLInputElement, ISearchInputProps>((props, ref) => {
  const [searchValue, setSearchValue] = useState<string>(props.value || '');
  const debouncedSearchValue = useDebounce(searchValue, 300);

  const { t } = useTranslation();

  useEffect(() => {
    props.onChange(debouncedSearchValue);
  }, [debouncedSearchValue]);

  return (
    <div className={cn('relative flex', styles.inputWrapper, props.className)}>
      <input
        ref={ref}
        className={cn(
          'flex w-full p-2 pl-10 text-sm font-light outline-none transition-all duration-200',
          'focus:font-normal focus:text-soft-black dark:focus:text-white',
          PaletteUtils.isInsigniaPalette && ['h-10 rounded border-b border-b-light bg-ice dark:bg-soft-black', 'hover:bg-white'],
          PaletteUtils.isVcfcPalette && [
            'h-12 rounded-[100px] border border-light/70 bg-white dark:border-transparent dark:bg-white/10',
            'hover:border-primary dark:hover:border-light/70',
          ],
          styles.input,
          props.inputClassName,
        )}
        placeholder={t('placeholders.search')}
        readOnly={props.readOnly}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onKeyDown={props.onKeyDown}
      />
      <Icon
        className={cn('absolute left-3 top-[10px]', PaletteUtils.isVcfcPalette && 'top-[14px]', styles.searchIcon)}
        icon={<SearchIcon />}
        color='light'
      />
    </div>
  );
});
